import {ElementPreset, Plugins} from '@packages/systems/wfdl';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';

import FeatureSectionIcon from './icons/layout-feature-section';

const prebuiltLayoutsPlugin = Plugins.name('PrebuiltLayouts');

import {FeaturesList, FeaturesMetrics, FeaturesTable} from './Features';

import {FooterDark, FooterLight, FooterSubscribe} from './Footer';

import {GalleryOverview, GalleryScroll, GallerySlider} from './Gallery';

import {
  HeroHeadingCenter,
  HeroHeadingLeft,
  HeroHeadingRight,
  HeroStack,
  HeroSubscribeLeft,
  HeroSubscribeRight,
  HeroWithoutImage,
} from './Hero';

import {
  LogosQuoteBlock,
  LogosQuoteDivider,
  LogosTitleLarge,
  LogosTitleSmall,
  LogosWithoutTitle,
} from './Logos';

import {NavbarLogoCenter, NavbarLogoLeft, NavbarNoShadow} from './Navbar';

import {PricingComparison, PricingItems, PricingOverview} from './Pricing';

import {TeamCircles, TeamSlider} from './Team';

import {
  TestimonialColumnDark,
  TestimonialColumnLight,
  TestimonialImageLeft,
  TestimonialSliderLarge,
  TestimonialSliderSmall,
  TestimonialStack,
} from './Testimonial';

import {
  type LibraryManifest,
  libraryManifestFromResource,
  getLibraryExportsFromPlugin,
  LIBRARY_SOURCE,
  LIBRARY_PROFILE_STATUS,
} from '@packages/systems/libraries';

const createElementPreset = (
  // @ts-expect-error - TS7031 - Binding element 'category' implicitly has an 'any' type. | TS7031 - Binding element 'displayName' implicitly has an 'any' type. | TS7031 - Binding element 'layout' implicitly has an 'any' type. | TS7031 - Binding element 'previewImageURL' implicitly has an 'any' type.
  {category, displayName, layout, previewImageURL},
  icon = 'FeatureSection'
) => {
  return ElementPreset.create({
    ...layout,
    category,
    prebuiltLayoutsImage: previewImageURL,
    icon: {
      small: qualify(prebuiltLayoutsPlugin, iconName(icon)),
      medium: qualify(prebuiltLayoutsPlugin, iconName(icon)),
    },
    label: displayName ?? '',
    helpData: {
      tag: 'section',
      description: [],
      screenshotTooltip: {
        screenshotFileName: previewImageURL,
        text: '',
      },
    },
  });
};

export const plugin = Plugins.PrivilegedPlugin({
  icons: {
    FeatureSection: FeatureSectionIcon, // Used as a default preview image
  },
  elementPresets: {
    // Navigation
    LayoutNavbarLogoCenter: createElementPreset(NavbarLogoCenter),
    LayoutNavbarLogoLeft: createElementPreset(NavbarLogoLeft),
    LayoutNavbarNoShadow: createElementPreset(NavbarNoShadow),

    // Hero
    LayoutHeroHeadingCenter: createElementPreset(HeroHeadingCenter),
    LayoutHeroHeadingLeft: createElementPreset(HeroHeadingLeft),
    LayoutHeroHeadingRight: createElementPreset(HeroHeadingRight),
    LayoutHeroStack: createElementPreset(HeroStack),
    LayoutHeroSubscribeLeft: createElementPreset(HeroSubscribeLeft),
    LayoutHeroSubscribeRight: createElementPreset(HeroSubscribeRight),
    LayoutHeroWithoutImage: createElementPreset(HeroWithoutImage),

    // Team
    LayoutTeamCircles: createElementPreset(TeamCircles),
    LayoutTeamSlider: createElementPreset(TeamSlider),

    // Logos
    LayoutLogosQuoteBlock: createElementPreset(LogosQuoteBlock),
    LayoutLogosQuoteDivider: createElementPreset(LogosQuoteDivider),
    LayoutLogosTitleLarge: createElementPreset(LogosTitleLarge),
    LayoutLogosTitleSmall: createElementPreset(LogosTitleSmall),
    LayoutLogosWithoutTitle: createElementPreset(LogosWithoutTitle),

    // Gallery
    LayoutGalleryOverview: createElementPreset(GalleryOverview),
    LayoutGalleryScroll: createElementPreset(GalleryScroll),
    LayoutGallerySlider: createElementPreset(GallerySlider),

    // Features
    LayoutFeaturesList: createElementPreset(FeaturesList),
    LayoutFeaturesMetrics: createElementPreset(FeaturesMetrics),
    LayoutFeaturesTable: createElementPreset(FeaturesTable),

    // Pricing
    LayoutPricingComparison: createElementPreset(PricingComparison),
    LayoutPricingItems: createElementPreset(PricingItems),
    LayoutPricingOverview: createElementPreset(PricingOverview),

    // Testimonial
    LayoutTestimonialColumnDark: createElementPreset(TestimonialColumnDark),
    LayoutTestimonialColumnLight: createElementPreset(TestimonialColumnLight),
    LayoutTestimonialImageLeft: createElementPreset(TestimonialImageLeft),
    LayoutTestimonialSliderLarge: createElementPreset(TestimonialSliderLarge),
    LayoutTestimonialSliderSmall: createElementPreset(TestimonialSliderSmall),
    LayoutTestimonialStack: createElementPreset(TestimonialStack),

    // Footer
    LayoutFooterDark: createElementPreset(FooterDark),
    LayoutFooterLight: createElementPreset(FooterLight),
    LayoutFooterSubscribe: createElementPreset(FooterSubscribe),
  },
});

export const libraryManifest: LibraryManifest<string> =
  libraryManifestFromResource({
    id: 'PrebuiltLayouts',
    profileId: 'PrebuiltLayouts',
    type: 'LIBRARY',
    displayName: 'Starter library',
    version: '1.0.0',
    plugin: {
      type: 'CONTENT',
      value: plugin,
    },
    exports: getLibraryExportsFromPlugin(plugin),
    // It's actually Builtin but we treat it as a Marketplace library
    source: LIBRARY_SOURCE.MARKETPLACE,
    profileStatus: LIBRARY_PROFILE_STATUS.SHARED,
  });
